import React, { useState, useEffect, useRef } from 'react';
import { providersAPI } from '../../api/providers';
import { uploadImage } from '../../utils/cloudinary';
import Input from '../../components/common/Input';
import Textarea from '../../components/common/Textarea';
import Select from '../../components/common/Select';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import MapPicker from '../../components/common/MapPicker';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import {
  BuildingStorefrontIcon,
  MapPinIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const LANGUAGES = [
  { id: 'English', name: 'English' },
  { id: 'Luganda', name: 'Luganda' },
  { id: 'Swahili', name: 'Swahili' },
  { id: 'French', name: 'French' },
  { id: 'Runyankole', name: 'Runyankole' },
  { id: 'Arabic', name: 'Arabic' },
];

const SPECIALIZATIONS = [
  'Hair Styling', 'Nail Care', 'Makeup Artistry', 'Facial Treatments',
  'Massage Therapy', 'Waxing/Depilation', 'Pedicure', 'Barbering',
  'Bridal Makeup', 'Dreadlocks', 'Hair Colouring', 'Tattoo Art',
];

const GENDER_OPTIONS = [
  { value: 'any', label: 'All Genders' },
  { value: 'male_only', label: 'Male Clients Only' },
  { value: 'female_only', label: 'Female Clients Only' },
];

const BusinessProfile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [provider, setProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoError, setPhotoError] = useState('');
  const profilePhotoRef = useRef(null);

  const [form, setForm] = useState({
    businessName: '',
    description: '',
    phone: '',
    location: '',
    shopAddress: '',
    lat: null,
    lng: null,
    offersMobile: false,
    maxTravelKm: '',
    languages: [],
    specializations: [],
    genderPreference: 'any',
    hygieneNotes: '',
    autoAcceptBookings: false,
    bufferTimeMinutes: 15,
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const data = await providersAPI.getMyProfile();
      setProvider(data);
      setForm({
        businessName: data.businessName || '',
        description: data.description || '',
        phone: data.phone || '',
        location: data.location || '',
        shopAddress: data.shopAddress || '',
        lat: data.lat || null,
        lng: data.lng || null,
        offersMobile: data.offersMobile || false,
        maxTravelKm: data.maxTravelKm || '',
        languages: data.languages || [],
        specializations: data.specializations || [],
        genderPreference: data.genderPreference || 'any',
        hygieneNotes: data.hygieneNotes || '',
        autoAcceptBookings: data.autoAcceptBookings || false,
        bufferTimeMinutes: data.bufferTimeMinutes || 15,
      });

    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleLocationSelect = (lat, lng, address) => {
    setForm(prev => ({ ...prev, lat, lng, shopAddress: address, location: address }));
  };

  const toggleLanguage = (lang) => {
    setForm(prev => ({
      ...prev,
      languages: prev.languages.includes(lang)
        ? prev.languages.filter(l => l !== lang)
        : [...prev.languages, lang],
    }));
  };

  const toggleSpecialization = (spec) => {
    setForm(prev => ({
      ...prev,
      specializations: prev.specializations.includes(spec)
        ? prev.specializations.filter(s => s !== spec)
        : [...prev.specializations, spec],
    }));
  };

  const handleSave = async () => {
    if (!form.businessName.trim()) {
      setErrors({ businessName: 'Business name is required' });
      setActiveTab('info');
      return;
    }

    setSaving(true);
    try {
      // Only send non-empty fields to avoid Joi empty string rejections
      const payload = {};
      if (form.businessName.trim()) payload.businessName = form.businessName.trim();
      if (form.description.trim()) payload.description = form.description.trim();
      if (form.location.trim()) payload.location = form.location.trim();
      if (form.shopAddress.trim()) payload.shopAddress = form.shopAddress.trim();
      if (form.lat !== null) payload.lat = form.lat;
      if (form.lng !== null) payload.lng = form.lng;
      if (form.phone && form.phone.trim()) payload.phone = form.phone.trim();
      if (form.hygieneNotes.trim()) payload.hygieneNotes = form.hygieneNotes.trim();
      if (form.genderPreference) payload.genderPreference = form.genderPreference;
      if (form.languages.length > 0) payload.languages = form.languages;
      if (form.specializations.length > 0) payload.specializations = form.specializations;
      if (form.maxTravelKm) payload.maxTravelKm = parseFloat(form.maxTravelKm);
      payload.offersMobile = form.offersMobile;
      payload.autoAcceptBookings = form.autoAcceptBookings;
      payload.bufferTimeMinutes = parseInt(form.bufferTimeMinutes) || 15;

      await providersAPI.updateMyProfile(payload);
      toast.success('Profile updated successfully');
      fetchProfile();
    } catch (error) {
      console.error('Error saving profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleProfilePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError('');
    if (!file.type.startsWith('image/')) { setPhotoError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setPhotoError('Image must be under 5MB'); return; }

    setUploadingProfile(true);
    try {
      const url = await uploadImage(file);
      await providersAPI.updateMyProfile({ photoUrl: url });
      setPhotoError('');
      toast.success('Profile photo updated');
      window.dispatchEvent(new CustomEvent('profilePhotoUpdated', { detail: url }));
      fetchProfile();
    } catch (error) {
      console.error('Error uploading profile photo:', error);
      toast.error('Failed to upload profile photo');
    } finally {
      setUploadingProfile(false);
      if (profilePhotoRef.current) profilePhotoRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="large" />
      </div>
    );
  }

  const tabs = [
    { id: 'info', label: 'Business Info', icon: BuildingStorefrontIcon },
    { id: 'location', label: 'Location', icon: MapPinIcon },
    { id: 'services', label: 'Preferences', icon: UserGroupIcon },
    { id: 'settings', label: 'Settings', icon: ClockIcon },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Business Profile</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Manage your business information, location, and preferences
          </p>
        </div>

        {/* Verification badge */}
        {provider && (
          <div
            className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
            style={{
              backgroundColor: provider.isApproved ? 'var(--green-2, #f0fdf4)' : 'var(--orange-2)',
              border: `1px solid ${provider.isApproved ? 'var(--green-6, #bbf7d0)' : 'var(--orange-5)'}`,
            }}
          >
            <ShieldCheckIcon style={{ width: '20px', height: '20px', color: provider.isApproved ? '#22c55e' : 'var(--orange-9)' }} />
            <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
              {provider.isApproved ? 'Verified Provider' : `Status: ${provider.verificationStatus}`}
            </span>
          </div>
        )}

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >

          {/* Business Info Tab */}
          {activeTab === 'info' && (
            <div className="space-y-6">
              {/* Profile Photo */}
              <div className="flex items-center gap-5">
                <div style={{ position: 'relative' }}>
                  {provider?.photoUrl ? (
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      outline: '3px solid var(--orange-6)', outlineOffset: '2px',
                      overflow: 'hidden', flexShrink: 0,
                    }}>
                      <img
                        src={provider.photoUrl}
                        alt={provider.businessName}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '80px', height: '80px', borderRadius: '50%',
                        backgroundColor: 'var(--orange-3)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '28px', fontWeight: 700, color: 'var(--orange-9)',
                        border: '3px solid var(--orange-6)', flexShrink: 0,
                      }}
                    >
                      {provider?.businessName?.charAt(0)?.toUpperCase() || 'P'}
                    </div>
                  )}
                </div>
                <div>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 6px 0', textAlign: 'left' }}>
                    Profile Photo
                  </p>
                  <input ref={profilePhotoRef} type="file" accept="image/*" onChange={handleProfilePhotoUpload} style={{ display: 'none' }} />
                  <button
                    onClick={() => profilePhotoRef.current?.click()}
                    disabled={uploadingProfile}
                    style={{
                      padding: '6px 16px', borderRadius: '8px', border: '1px solid var(--orange-6)',
                      backgroundColor: 'transparent', color: 'var(--orange-9)', fontSize: '13px',
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {uploadingProfile ? 'Uploading...' : provider?.photoUrl ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {photoError && (
                    <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', margin: '6px 0 0', textAlign: 'left' }}>
                      {photoError}
                    </p>
                  )}
                </div>
              </div>

              <Input
                label="Business Name"
                name="businessName"
                value={form.businessName}
                onChange={handleChange}
                placeholder="e.g. Elite Salon & Spa"
                error={errors.businessName}
                required
              />

              <Textarea
                label="Business Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Tell clients about your business, experience, and what makes you unique..."
                rows={4}
              />

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
                  Phone Number
                </label>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="UG"
                  value={form.phone}
                  onChange={(value) => setForm(prev => ({ ...prev, phone: value || '' }))}
                  className="phone-input-field"
                />
              </div>

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '8px', textAlign: 'left' }}>
                  Specializations
                </label>
                <div className="flex flex-wrap gap-2">
                  {SPECIALIZATIONS.map(spec => (
                    <button
                      key={spec}
                      type="button"
                      onClick={() => toggleSpecialization(spec)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        backgroundColor: form.specializations.includes(spec) ? 'var(--orange-9)' : 'var(--gray-4)',
                        color: form.specializations.includes(spec) ? '#fff' : 'var(--gray-11)',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {spec}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="space-y-6">
              <Input
                label="Business Address"
                name="shopAddress"
                value={form.shopAddress}
                onChange={handleChange}
                placeholder="e.g. 86g Gayaza Road, Kampala"
              />

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '8px', textAlign: 'left' }}>
                  Pin Your Location
                </label>
                <MapPicker
                  onLocationSelect={handleLocationSelect}
                  initialLat={form.lat || undefined}
                  initialLng={form.lng || undefined}
                  initialAddress={form.shopAddress}
                />
              </div>

              <div className="flex items-center gap-3" style={{ paddingTop: '8px' }}>
                <input
                  type="checkbox"
                  name="offersMobile"
                  checked={form.offersMobile}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--orange-9)' }}
                />
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                    I offer mobile services
                  </p>
                  <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                    I can travel to the client's location
                  </p>
                </div>
              </div>

              {form.offersMobile && (
                <Input
                  label="Maximum Travel Distance (km)"
                  type="number"
                  name="maxTravelKm"
                  value={form.maxTravelKm}
                  onChange={handleChange}
                  placeholder="e.g. 15"
                />
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'services' && (
            <div className="space-y-6">
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '8px', textAlign: 'left' }}>
                  Languages Spoken
                </label>
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map(({ id, name }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleLanguage(id)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: '20px',
                        border: 'none',
                        fontSize: '13px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        backgroundColor: form.languages.includes(id) ? 'var(--orange-9)' : 'var(--gray-4)',
                        color: form.languages.includes(id) ? '#fff' : 'var(--gray-11)',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              <Select
                label="Gender Preference"
                name="genderPreference"
                value={form.genderPreference}
                onChange={handleChange}
                options={GENDER_OPTIONS}
              />

              <Textarea
                label="Hygiene & Safety Notes"
                name="hygieneNotes"
                value={form.hygieneNotes}
                onChange={handleChange}
                placeholder="Describe your hygiene practices, sterilization methods, and safety measures..."
                rows={3}
              />
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between" style={{ padding: '12px 0', borderBottom: '1px solid var(--gray-4)' }}>
                <div>
                  <p style={{ fontSize: '15px', fontWeight: 500, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                    Auto-accept bookings
                  </p>
                  <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>
                    Automatically confirm new booking requests without manual approval
                  </p>
                </div>
                <input
                  type="checkbox"
                  name="autoAcceptBookings"
                  checked={form.autoAcceptBookings}
                  onChange={handleChange}
                  style={{ width: '20px', height: '20px', accentColor: 'var(--orange-9)' }}
                />
              </div>

              <div>
                <Input
                  label="Buffer Time Between Appointments (minutes)"
                  type="number"
                  name="bufferTimeMinutes"
                  value={form.bufferTimeMinutes}
                  onChange={handleChange}
                  placeholder="15"
                />
                <p style={{ fontSize: '13px', color: 'var(--gray-10)', marginTop: '4px', textAlign: 'left' }}>
                  Time gap between consecutive bookings for preparation
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Save button — always visible */}
        <div className="flex justify-end mt-6">
          <Button onClick={handleSave} loading={saving} size="large">
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessProfile;
